/** /activity_categories/ — cross-tenant Activity_Category catalog.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). Reads, create and delete; an edit is the category
 * changeset (`activityCategoryRecord.ts`) — the console never PATCHes a
 * category. Deleting a category SET_NULLs its activities' `category`
 * (surface `activity_count` / used_by as a warning); a 409 `category_in_use`
 * only occurs if the backend cannot detach a reference. The icon uses the same
 * `{p,s}` → URL mechanism the trip-type editor uses (`imageUrl` util).
 */
import { apiDelete, apiGet, client } from './client';
import type { NamedRef, TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

/** Image path chunks — compose the URL as `${AWS_IMAGE_BASE_URL}${p}${s}`
 *  (see `imageUrl` in `@/utils/imageUrl`). */
export interface ImageRef {
  p: string;
  s: string;
}

export interface ActivityCategoryRow {
  id: number;
  tenant: TenantRef;
  title: string;
  icon: ImageRef | null;
  activity_count: number;
  atag: NamedRef[];
}

/** The category record (GET / POST): the list row plus the category in the
 *  operator's dashboard. */
export interface ActivityCategoryDetail extends ActivityCategoryRow {
  /** Null without an operator. */
  dashboard_url: string | null;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<ActivityCategoryRow>> {
  return apiGet<PaginatedResponse<ActivityCategoryRow>>(`/activity_categories/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<ActivityCategoryDetail> {
  return apiGet<ActivityCategoryDetail>(`/activity_categories/${id}/`);
}

/** POST /activity_categories/ body (the `_json` part when an icon File goes with it):
 *  what the create dialog sends. The rest is edited on the record. */
export interface ActivityCategoryWriteJson {
  tour_operator: number;
  title: string;
}

/** POST /activity_categories/ — plain JSON, or multipart (`_json` + `icon` file)
 *  when an icon File is staged. */
export async function create(
  body: ActivityCategoryWriteJson,
  icon?: File | null,
): Promise<ActivityCategoryDetail> {
  if (icon) {
    const fd = new FormData();
    fd.append('_json', JSON.stringify(body));
    fd.append('icon', icon);
    const r = await client.post<ActivityCategoryDetail>('/activity_categories/', fd, {
      headers: { 'Content-Type': undefined },
    });
    return r.data;
  }
  const r = await client.post<ActivityCategoryDetail>('/activity_categories/', body);
  return r.data;
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/activity_categories/${id}/`);
}
